import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const ProgramModal = ({ isOpen, onClose, program, onSave }) => {
  const { profile } = useAuth();
  const currentRoles = [profile?.primary_role, ...(profile?.secondary_roles || [])].filter(Boolean);
  const isHeadOfDepartment = currentRoles.includes('head_of_department');
  const assignedDepartmentId = profile?.department_id
    || profile?.department?.id
    || profile?.staff?.department_id
    || profile?.staff?.department?.id
    || '';
  const assignedDepartment = profile?.department || profile?.staff?.department || null;
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    levels: [],
    departmentId: '', // Changed from department to departmentId
    status: 'Active',
    capacity: '',
    levelDurations: {
      3: '',
      4: '',
      5: '',
      6: ''
    },
    levelCapacities: {
      3: '',
      4: '',
      5: '',
      6: ''
    },
    levelCodes: {
      3: '', 4: '', 5: '', 6: ''
    }
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]); // Fetch from database
  const [loadingDepartments, setLoadingDepartments] = useState(true);

  // Level mapping
  const levelOptions = [
    { value: '3', label: 'Level 3 (NVC)' },
    { value: '4', label: 'Level 4 (Artisan)' },
    { value: '5', label: 'Level 5 (Craft)' },
    { value: '6', label: 'Level 6 (Diploma)' }
  ];

  // Fetch departments from database
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        let departmentQuery = supabase
          ?.from('departments')
          ?.select('id, name, code')
          ?.order('name');

        if (isHeadOfDepartment && assignedDepartmentId) {
          departmentQuery = departmentQuery.eq('id', assignedDepartmentId);
        }

        const { data, error } = await departmentQuery;

        if (error) throw error;

        setDepartments(data?.length ? data : (assignedDepartment ? [assignedDepartment] : []));
      } catch (error) {
        console.error('Error fetching departments:', error);
        setErrors({ departments: 'Failed to load departments' });
      } finally {
        setLoadingDepartments(false);
      }
    };

    if (isOpen) {
      if (isHeadOfDepartment && !assignedDepartmentId) {
        setDepartments([]);
        setErrors(current => ({ ...current, departments: 'Your HoD account is not assigned to a department.' }));
        setLoadingDepartments(false);
      } else {
        fetchDepartments();
      }
    }
  }, [isOpen, isHeadOfDepartment, assignedDepartmentId, assignedDepartment]);

  useEffect(() => {
    if (!isOpen || !program?.id) return;
    supabase.from('program_admission_levels').select('level,program_level_code').eq('program_id',program.id)
      .then(({data,error})=>{if(error)throw error;setFormData(current=>({...current,levelCodes:(data||[]).reduce((codes,item)=>({...codes,[item.level]:item.program_level_code||''}),{...current.levelCodes})}));})
      .catch(error=>setErrors(current=>({...current,levelCodes:error.message||'Unable to load programme level codes'})));
  }, [isOpen,program?.id]);

  useEffect(() => {
    if (program) {
      // Map database data to form structure
      const levelsArray = program?.levels || [];
      const durationsObj = program?.levelDurations || {};
      const capacitiesObj = program?.levelCapacities || {};

      // Convert level names to level numbers for form
      const levelMapping = {
        'level_3': '3',
        'level_4_artisan': '4',
        'level_5_craft': '5',
        'level_6_diploma': '6'
      };

      const mappedLevels = levelsArray?.map(level => levelMapping?.[level] || level);

      // Normalize durations and capacities keys
      const normalizeKeys = (obj) => {
        const newObj = {};
        Object.keys(obj || {}).forEach(key => {
          const shortKey = levelMapping[key] || key;
          newObj[shortKey] = obj[key];
        });
        return newObj;
      };

      setFormData({
        name: program?.name || '',
        code: program?.code || '',
        levels: mappedLevels,
        departmentId: program?.departmentId || '', // Use departmentId from program
        status: program?.status || 'Active',
        capacity: program?.capacity?.toString() || '',
        levelDurations: normalizeKeys(durationsObj),
        levelCapacities: normalizeKeys(capacitiesObj),
        levelCodes: {3:'',4:'',5:'',6:''}
      });
    } else {
      setFormData({
        name: '',
        code: '',
        levels: [],
        departmentId: '', // Reset to empty
        status: 'Active',
        capacity: '',
        levelDurations: {
          3: '',
          4: '',
          5: '',
          6: ''
        },
        levelCapacities: {
          3: '',
          4: '',
          5: '',
          6: ''
        },
        levelCodes: {3:'',4:'',5:'',6:''}
      });
    }
    setErrors({});
  }, [program, isOpen]);

  useEffect(() => {
    if (!isOpen || program || !isHeadOfDepartment || !assignedDepartmentId) return;
    setFormData(current => ({
      ...current,
      departmentId: assignedDepartmentId
    }));
  }, [isOpen, program, isHeadOfDepartment, assignedDepartmentId]);

  const handleLevelToggle = (levelValue) => {
    setFormData(prev => {
      const newLevels = prev?.levels?.includes(levelValue)
        ? prev?.levels?.filter(l => l !== levelValue)
        : [...prev?.levels, levelValue];
      const cleanProgramCode=String(prev.code||'PROGRAM').replace(/[^a-zA-Z0-9]/g,'').toUpperCase();
      return { ...prev, levels: newLevels, levelCodes:{...prev.levelCodes,[levelValue]:prev.levelCodes?.[levelValue]||`${cleanProgramCode}L${levelValue}`} };
    });
  };

  const handleDurationChange = (level, value) => {
    setFormData(prev => ({
      ...prev,
      levelDurations: {
        ...prev?.levelDurations,
        [level]: value
      }
    }));
  };

  const handleCapacityChange = (level, value) => {
    setFormData(prev => {
      const newCapacities = {
        ...prev?.levelCapacities,
        [level]: value
      };

      // Calculate total capacity
      const total = Object.values(newCapacities).reduce((sum, cap) => {
        return sum + (parseInt(cap) || 0);
      }, 0);

      return {
        ...prev,
        levelCapacities: newCapacities,
        capacity: total > 0 ? total.toString() : ''
      };
    });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Program name is required';
    if (!formData?.code?.trim()) newErrors.code = 'Program code is required';
    if (!formData?.departmentId?.trim()) newErrors.departmentId = 'Department is required'; // Changed from department to departmentId
    if (!formData?.levels?.length) newErrors.levels = 'At least one level must be selected';
    if (!formData?.capacity || parseInt(formData?.capacity) <= 0) {
      newErrors.capacity = 'Valid capacity is required';
    }

    // Validate that selected levels have durations (modules)
    formData?.levels?.forEach(level => {
      if (!formData?.levelDurations?.[level]?.trim()) {
        newErrors[`duration_${level}`] = `Module count required for Level ${level}`;
      }
      if (!formData?.levelCapacities?.[level] || parseInt(formData?.levelCapacities?.[level]) <= 0) {
        newErrors[`capacity_${level}`] = `Capacity required for Level ${level}`;
      }
      if (!formData?.levelCodes?.[level]?.trim()) newErrors[`code_${level}`]=`Programme level code required for Level ${level}`;
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validate()) {
      // Keep the submit action obvious even when the form is scrolled to its
      // footer: move the first invalid control into view after React renders
      // the validation state.
      window.setTimeout(() => {
        document
          .querySelector('[data-program-invalid="true"]')
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 0);
      return;
    }

    setLoading(true);
    try {
      // Convert form level numbers back to database level names
      const levelReverseMapping = {
        '3': 'level_3',
        '4': 'level_4_artisan',
        '5': 'level_5_craft',
        '6': 'level_6_diploma'
      };

      const dbLevels = formData?.levels?.map(level => levelReverseMapping?.[level] || level);

      const programData = {
        code: formData?.code,
        name: formData?.name,
        levels: dbLevels,
        levelDurations: formData?.levelDurations,
        levelCapacities: formData?.levelCapacities,
        levelCodes: formData?.levelCodes,
        departmentId: formData?.departmentId, // Now using actual department ID
        status: formData?.status,
        capacity: parseInt(formData?.capacity),
        id: program?.id
      };

      await onSave(programData);
      onClose();
    } catch (error) {
      console.error('Error saving program:', error);
      const rawMessage = String(error?.message || '');
      const submitMessage = /row-level security|permission denied/i.test(rawMessage)
        ? 'Your account is not authorised to create programmes. Sign in with a programme-creator role or ask an administrator to grant programs_create permission.'
        : rawMessage || 'Failed to save program. Please try again.';
      setErrors({ submit: submitMessage });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const hasValidationErrors = Object.keys(errors).length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-[200] p-3 sm:p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-5xl w-full max-h-[calc(100dvh-1.5rem)] overflow-hidden flex flex-col">
        <div className="shrink-0 bg-card border-b border-border px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {program ? 'Edit Program' : 'Add New Program'}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="min-h-0 overflow-y-auto p-4 sm:p-5 space-y-4">
          {hasValidationErrors && (
            <div
              role="alert"
              className="bg-error/10 border border-error/30 rounded-lg px-3 py-2 text-sm text-error"
            >
              Please complete the highlighted required fields before creating the program.
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-foreground">Program Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              <div className="xl:col-span-1">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Program Name *
                </label>
                <Input
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  placeholder="e.g., Electrical Engineering"
                  className={errors?.name ? 'border-error' : ''}
                  aria-invalid={Boolean(errors?.name)}
                  data-program-invalid={errors?.name ? 'true' : undefined}
                />
                {errors?.name && (
                  <p className="text-sm text-error mt-1">{errors?.name}</p>
                )}
              </div>

              <div className="xl:col-span-1">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Program Code *
                </label>
                <Input
                  value={formData?.code}
                  onChange={(e) => setFormData({ ...formData, code: e?.target?.value })}
                  placeholder="e.g., EED-301"
                  className={errors?.code ? 'border-error' : ''}
                  aria-invalid={Boolean(errors?.code)}
                  data-program-invalid={errors?.code ? 'true' : undefined}
                />
                {errors?.code && (
                  <p className="text-sm text-error mt-1">{errors?.code}</p>
                )}
              </div>

              <div className="xl:col-span-1">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Department * {isHeadOfDepartment && <span className="text-xs font-normal text-muted-foreground">(assigned to your HoD account)</span>}
                </label>
                {isHeadOfDepartment ? (
                  <div
                    className={`w-full px-3 py-2 border rounded-md bg-muted text-foreground ${errors?.departmentId ? 'border-error' : 'border-border'}`}
                    aria-invalid={Boolean(errors?.departmentId)}
                    data-program-invalid={errors?.departmentId ? 'true' : undefined}
                  >
                    {loadingDepartments
                      ? 'Loading your department...'
                      : departments?.[0]
                        ? `${departments[0].name}${departments[0].code ? ` (${departments[0].code})` : ''}`
                        : assignedDepartment?.name
                          ? `${assignedDepartment.name}${assignedDepartment.code ? ` (${assignedDepartment.code})` : ''}`
                          : 'No department assigned'}
                  </div>
                ) : (
                  <select
                    value={formData?.departmentId}
                    onChange={(e) => setFormData({ ...formData, departmentId: e?.target?.value })}
                    className={`w-full px-3 py-2 border rounded-md bg-input text-foreground ${errors?.departmentId ? 'border-error' : 'border-border'
                    }`}
                    disabled={loadingDepartments}
                    aria-invalid={Boolean(errors?.departmentId)}
                    data-program-invalid={errors?.departmentId ? 'true' : undefined}
                  >
                    <option value="">
                      {loadingDepartments ? 'Loading departments...' : 'Select Department'}
                    </option>
                    {departments?.map((dept) => (
                      <option key={dept?.id} value={dept?.id}>
                        {dept?.name} ({dept?.code})
                      </option>
                    ))}
                  </select>
                )}
                {errors?.departmentId && (
                  <p className="text-sm text-error mt-1">{errors?.departmentId}</p>
                )}
                {errors?.departments && (
                  <p className="text-sm text-error mt-1">{errors?.departments}</p>
                )}
              </div>

              <div className="xl:col-span-1">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Status *
                </label>
                <select
                  value={formData?.status}
                  onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>

              <div className="xl:col-span-1">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Total Capacity * <span className="text-xs text-muted-foreground">(Auto-calculated)</span>
                </label>
                <Input
                  type="number"
                  value={formData?.capacity}
                  readOnly
                  className="bg-muted text-muted-foreground"
                  placeholder="0"
                  aria-invalid={Boolean(errors?.capacity)}
                  data-program-invalid={errors?.capacity ? 'true' : undefined}
                />
                {errors?.capacity && (
                  <p className="text-sm text-error mt-1">{errors?.capacity}</p>
                )}
              </div>
            </div>
          </div>

          {/* Program Levels - Multi-select */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Program Levels * <span className="text-muted-foreground text-xs">(Select all levels this program is offered at)</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                {levelOptions?.map((level) => (
                  <div
                    key={level?.value}
                    data-program-invalid={errors?.levels && level?.value === levelOptions?.[0]?.value ? 'true' : undefined}
                    className={`border rounded-lg p-3 cursor-pointer transition-all ${formData?.levels?.includes(level?.value)
                      ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                      }`}
                    onClick={() => handleLevelToggle(level?.value)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${formData?.levels?.includes(level?.value)
                        ? 'border-primary bg-primary' : 'border-border'
                        }`}>
                        {formData?.levels?.includes(level?.value) && (
                          <Icon name="Check" size={14} className="text-white" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {level?.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {errors?.levels && (
                <p className="text-sm text-error mt-2">{errors?.levels}</p>
              )}
            </div>

            {/* Duration per Level */}
            {formData?.levels?.length > 0 && (
              <div className="space-y-2 mt-3">
                <label className="block text-sm font-medium text-foreground">
                  Duration by Level *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {formData?.levels?.map((levelValue) => {
                    const levelLabel = levelOptions?.find(l => l?.value === levelValue)?.label;
                    return (
                      <div key={levelValue} className="p-3 border rounded-lg space-y-2">
                        <label className="block font-medium text-primary">
                          {levelLabel}
                        </label>

                        <div><label className="block text-xs text-muted-foreground mb-1">Programme level code *</label><Input value={formData?.levelCodes?.[levelValue]||''} onChange={e=>setFormData(current=>({...current,levelCodes:{...current.levelCodes,[levelValue]:e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,'')}}))} placeholder={`e.g. ${String(formData.code||'PROGRAM').replace(/[^a-zA-Z0-9]/g,'').toUpperCase()}L${levelValue}`} className={errors?.[`code_${levelValue}`]?'border-error':''} aria-invalid={Boolean(errors?.[`code_${levelValue}`])} data-program-invalid={errors?.[`code_${levelValue}`]?'true':undefined}/>{errors?.[`code_${levelValue}`]&&<p className="text-xs text-error mt-1">{errors[`code_${levelValue}`]}</p>}</div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Modules</label>
                            <Input
                              type="number"
                              min="1"
                              value={formData?.levelDurations?.[levelValue] || ''}
                              onChange={(e) => handleDurationChange(levelValue, e?.target?.value)}
                              placeholder="e.g., 4"
                              className={errors?.[`duration_${levelValue}`] ? 'border-error' : ''}
                              aria-invalid={Boolean(errors?.[`duration_${levelValue}`])}
                              data-program-invalid={errors?.[`duration_${levelValue}`] ? 'true' : undefined}
                            />
                            {errors?.[`duration_${levelValue}`] && (
                              <p className="text-xs text-error mt-1">{errors?.[`duration_${levelValue}`]}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Capacity</label>
                            <Input
                              type="number"
                              min="1"
                              value={formData?.levelCapacities?.[levelValue] || ''}
                              onChange={(e) => handleCapacityChange(levelValue, e?.target?.value)}
                              placeholder="e.g., 40"
                              className={errors?.[`capacity_${levelValue}`] ? 'border-error' : ''}
                              aria-invalid={Boolean(errors?.[`capacity_${levelValue}`])}
                              data-program-invalid={errors?.[`capacity_${levelValue}`] ? 'true' : undefined}
                            />
                            {errors?.[`capacity_${levelValue}`] && (
                              <p className="text-xs text-error mt-1">{errors?.[`capacity_${levelValue}`]}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {errors?.submit && (
            <div className="bg-error/10 border border-error rounded-lg p-3">
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="sticky bottom-0 bg-card flex items-center justify-between gap-3 pt-3 border-t border-border">
            <p className="text-xs text-error" aria-live="polite">
              {hasValidationErrors ? 'Required fields are marked above.' : ''}
            </p>
            <div className="flex items-center justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading || loadingDepartments}
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  {program ? 'Update Program' : 'Create Program'}
                </>
              )}
            </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProgramModal;
